import { useState } from "react";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { Plus, X } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_ADD_FORMAT,
  TEMPLATES_REMOVE_FORMAT,
} from "../../../i18n/keysTranslations";
import {
  getArtboardLabel,
  listArtboardIDs,
  listAvailablePresets,
} from "../utils/artboards";

const chipSx = ({ active, disabled }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  height: 32,
  px: 1.25,
  borderRadius: "8px",
  border: "1px solid",
  borderColor: active ? "#18181B" : "#E4E4E7",
  bgcolor: active ? "#18181B" : "#fff",
  color: active ? "#fff" : "text.primary",
  fontSize: "13px",
  fontWeight: 500,
  fontFamily: "inherit",
  appearance: "none",
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  userSelect: "none",
});

const ArtboardSwitcher = ({
  artboards = {},
  activeArtboardID,
  disabled,
  onSelect,
  onAddPreset,
  onRemoveActive,
}) => {
  const { t } = useTranslationApp();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const artboardIDs = listArtboardIDs({ artboards });
  const availablePresets = listAvailablePresets({ artboards });
  const canRemove = artboardIDs.length > 1;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
      {artboardIDs.map((id) => {
        const active = id === activeArtboardID;
        return (
          <Box
            key={id}
            component="button"
            type="button"
            disabled={disabled}
            onClick={() => onSelect(id)}
            sx={{
              ...chipSx({ active, disabled }),
            }}
          >
            {getArtboardLabel({ artboard: artboards[id], artboardID: id })}
            {active && canRemove ? (
              <Box
                component="span"
                onClick={(event) => {
                  event.stopPropagation();
                  if (disabled) return;
                  onRemoveActive();
                }}
                sx={{
                  display: "inline-flex",
                  ml: 0.25,
                  opacity: 0.8,
                  "&:hover": { opacity: 1 },
                }}
                title={t(TEMPLATES_REMOVE_FORMAT)}
              >
                <X size={12} />
              </Box>
            ) : null}
          </Box>
        );
      })}
      {availablePresets.length > 0 ? (
        <>
          <Box
            component="button"
            type="button"
            disabled={disabled}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={chipSx({ active: false, disabled })}
            title={t(TEMPLATES_ADD_FORMAT)}
          >
            <Plus size={14} />
            {t(TEMPLATES_ADD_FORMAT)}
          </Box>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {availablePresets.map((preset) => (
              <MenuItem
                key={preset.id}
                onClick={() => {
                  setMenuAnchor(null);
                  onAddPreset(preset.id);
                }}
              >
                {preset.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : null}
    </Box>
  );
};

export default ArtboardSwitcher;
